import React, { useState } from "react";
import {
    Tooltip,
    Drawer,
    IconButton,
    Typography,
    TextField,
    ToggleButtonGroup,
    ToggleButton,
    FormControl,
    Button,
    Box,
    Stack, Chip, Divider,
    Autocomplete,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { SearchData, PresenceFilter } from '../models/SearchElements';
import { useReferenceData } from '../components/ReferenceDataContext';
import { useApiClient } from "../components/useApiClient";
import { useUser } from '../components/UserContext';
import type { AppLocation } from "../models/AppLocation";
import { DrawerLocationsMulti } from "./DrawerLocationsMulti";
import { DrawerCategoryMulti } from "./DrawerCategoryMulti";
import type { Artist } from "../models/artist";
import { ArtistFilter } from "../components/Filter/ArtistFilter";
import { DateRangeFilter } from "../components/Filter/DateRangeFilter";
import { useFilter } from '../components/Filter/ArtworkFilterContext';
import ValueRangeFilter, { ValueRangeFilterState } from '../components/Filter/ValueRangeFilter';
import { ARTWORK_STATUSES, ARTWORK_STATUS_CATEGORY_LABELS, ArtworkStatusInfo } from "../functions/ArtworkStatus";
interface DrawerSearchProps {
    defaultCurrency: string;
}
export const DrawerSearch: React.FC<DrawerSearchProps> = ({
    defaultCurrency,
}) => {
    const { get } = useApiClient();
    const { activeClientId, getAccessToken, isFinance } = useUser();
    const { locations, categories, currencies, isReady, loadingAny, errorAny, refresh } = useReferenceData();
    const [availableArtists, setAvailableArtists] = React.useState<Artist[]>([]);
    const [locationIds, setLocationIds] = React.useState<number[]>([]);
    const [categoryIds, setCategoryIds] = React.useState<number[]>([]);
    const [artistIds, setArtistIds] = React.useState<number[]>([]);
    const [statusIds, setStatusIds] = React.useState<number[]>([]);
    const [locPickerOpen, setLocPickerOpen] = React.useState(false);
    const [catPickerOpen, setCatPickerOpen] = React.useState(false);
    const removeLocation = (id: number) => setLocationIds((xs) => xs.filter((x) => x !== id));
    const removeCategory = (id: number) => setCategoryIds((xs) => xs.filter((x) => x !== id));
    const [idToLabel, setIdToLabel] = React.useState<Map<number, string>>(new Map());
    const [idToCatLabel, setIdToCatLabel] = React.useState<Map<number, string>>(new Map());
    const [valueFilter, setValueFilter] = useState<ValueRangeFilterState | null>(null);
    const [acquiredFromDateFilter, setAcquiredFromDateFilter] = useState<string | null | undefined>(null);
    const [acquiredToDateFilter, setAcquiredToDateFilter] = useState<string | null | undefined>(null);
    const { filterParams, setFilterParams } = useFilter();
    const [imageStatus, setImageStatus] = React.useState<PresenceFilter>("any");
    const [acquisitionValueStatus, setAcquisitionValueStatus] = React.useState<PresenceFilter>("any");
    const [insuranceValueStatus, setInsuranceValueStatus] = React.useState<PresenceFilter>("any");
    const [fairMarketValueStatus, setFairMarketValueStatus] = React.useState<PresenceFilter>("any");
    const clientIdRef = React.useRef<number | null>(null);
    React.useEffect(() => { clientIdRef.current = activeClientId; }, [activeClientId]);
    React.useEffect(() => {
        const getClientArtists = async () => {
            const accessToken = await getAccessToken();
            if (accessToken) {
                const artists = await get<Artist[]>(`artists/for-client/${clientIdRef.current}`, accessToken);
                setAvailableArtists(artists);
            }
        }
        getClientArtists();
    }, [activeClientId]);
    React.useEffect(() => {
        //Set Selected Categories
        setCategoryIds(filterParams.categoryIds ?? []);
        //Set Selected Locations
        setLocationIds(filterParams.locations ?? []);
        //Set Selected Artists
        setArtistIds(filterParams.artistIds ?? []);
        //Set Selected Statuses
        setStatusIds(filterParams.statusIds ?? []);
        //Acquired Date Range
        setAcquiredFromDateFilter(filterParams.acquiredFrom);
        setAcquiredToDateFilter(filterParams.acquiredTo);
        //Initial State of Value Filter on Entry
        setValueFilter({
            min: filterParams.minValue,
            max: filterParams.maxValue,
            currency: filterParams.currency ?? defaultCurrency,
            valuationType: filterParams.valuationType //"acquisition"
        })
        //Set Presence Indicators
        setImageStatus(filterParams.imageStatus ?? "any");
        setAcquisitionValueStatus(filterParams.acquisitionValueStatus ?? "any");
        setInsuranceValueStatus(filterParams.insuranceValueStatus ?? "any");
        setFairMarketValueStatus(filterParams.fairMarketValueStatus ?? "any");
    }, [filterParams]);
    React.useEffect(() => {
        // Build once when locations change
        const buildMap = (input: any[]): Map<number, string> => {
            const m = new Map<number, string>();
            const walk = (n: any) => {
                const id = Number(n.id);
                const name = String(n.locationName ?? n.label ?? "");
                if (Number.isFinite(id)) m.set(id, name);
                (n.children ?? []).forEach(walk);
            };
            (locations ?? []).forEach(walk);
            return m;
        };
        if (locations) setIdToLabel(buildMap(locations as any[]));
    }, [locations]);
    React.useEffect(() => {
        // Build once when categories change
        const buildMap = (input: any[]): Map<number, string> => {
            const m = new Map<number, string>();
            const walk = (n: any) => {
                const id = Number(n.id);
                const name = String(n.name ?? n.label ?? "");
                if (Number.isFinite(id)) m.set(id, name);
                (n.children ?? []).forEach(walk);
            };
            (categories ?? []).forEach(walk);
            return m;
        };
        if (categories) setIdToCatLabel(buildMap(categories as any[]));
    }, [categories]);
    // Get all IDs under a selected location
    const getAllLocationIds = (loc: AppLocation): string[] => {
        let ids: string[] = [loc.id];
        if (loc.children) {
            for (const child of loc.children) {
                ids = ids.concat(getAllLocationIds(child));
            }
        }
        return ids;
    };
    const findLocationById = (
        locations: AppLocation[],
        id: string
    ): AppLocation | undefined => {
        for (const loc of locations) {
            if (loc.id === id) return loc;
            if (loc.children) {
                const found = findLocationById(loc.children, id);
                if (found) return found;
            }
        }
        return undefined;
    };
    const [open, setOpen] = useState(false);
    const [filters, setFilters] = useState<SearchData>(filterParams);
    const toggleDrawer = (state: boolean) => () => {
        setOpen(state);
    };
    const handleChange = (field: keyof SearchData, value: any) => {
        setFilters({ ...filters, [field]: value });
    };
    const handleArtistChange = (ids: number[]) => {
        setArtistIds(ids);
    };
    const handleStatusChange = (statuses: ArtworkStatusInfo[]) => {
        setStatusIds(statuses.map((s) => s.artworkStatusId));
    };
    const handleDateRangeChange = (from: string | null, to: string | null) => {
        setAcquiredFromDateFilter(from);
        setAcquiredToDateFilter(to);
    };
    const handleSearch = () => {
        setOpen(false);
        const searchData: SearchData = {
            description: filters.description,
            categoryIds: categoryIds,
            locations: locationIds,
            artistIds: artistIds,
            statusIds: statusIds.length ? statusIds : null,
            minValue: valueFilter?.min || null,
            maxValue: valueFilter?.max || null,
            currency: valueFilter?.currency,
            portfolioId: filterParams.portfolioId,
            valuationType: valueFilter?.valuationType || "acquisition",
            acquiredFrom: acquiredFromDateFilter,
            acquiredTo: acquiredToDateFilter,
            imageStatus: imageStatus,
            acquisitionValueStatus: acquisitionValueStatus,
            insuranceValueStatus: insuranceValueStatus,
            fairMarketValueStatus: fairMarketValueStatus,
        };
        setFilterParams(searchData);
    };
    const handleClearSearch = () => {
        setLocationIds([]);
        setCategoryIds([]);
        setStatusIds([]);
        setFilterParams({
            description: "",
            categoryIds: [],
            locations: [],
            statusIds: null,
            minValue: null,
            maxValue: null,
            currency: defaultCurrency,
            valuationType: "acquisition",
            imageStatus: "any",
            acquisitionValueStatus: "any",
            insuranceValueStatus: "any",
            fairMarketValueStatus: "any",
        });
        setValueFilter({
            min: null,
            max: null,
            currency: defaultCurrency,
            valuationType: "acquisition"
        })
    };
    const handleFilterChange = (field: string, value: PresenceFilter) => {
        switch (field) {
            case "imageStatus":
                setImageStatus(value);
                break;
            case "acquisitionValueStatus":
                setAcquisitionValueStatus(value);
                break;
            case "insuranceValueStatus":
                setInsuranceValueStatus(value);
                break;
            case "fairMarketValueStatus":
                setFairMarketValueStatus(value);
                break;
            default:
                break;
        }
    }
    const handleValueFilterChange = (filter: ValueRangeFilterState | null) => {
        setValueFilter(filter);
    };
    const selectedStatuses = ARTWORK_STATUSES.filter((s) => statusIds.includes(s.artworkStatusId));
    if (!isReady && loadingAny) return <p>Loading reference data...</p>
    if (!isReady && errorAny) {
        return (
            <div className="bg-yellow-50 border border-yellow-300 p-3 rounded">
                <p>{errorAny}</p>
                <button className="mt-2 px-3 py-1 rounded border" onClick={() => refresh()}>
                    Retry loading references
                </button>
            </div>
        );
    }
    return (
        <>
            <Tooltip title="Search">
                {/*<IconButton color="inherit" onClick={toggleDrawer(true)}>*/}
                {/*    <SearchIcon />*/}
                {/*    </IconButton>*/}
                <button className="icon-btn" aria-label="Filter" onClick={toggleDrawer(true)}>
                    <SearchIcon />
                    <span>Search</span>
                </button>
            </Tooltip>
            <Drawer anchor="right" open={open} onClose={toggleDrawer(false)}>
                <Box
                    sx={{
                        width: { xs: "100vw", sm: 400 }, // full width on mobile, 400px on tablet/desktop
                        maxWidth: "100%",
                        padding: 2,
                        boxSizing: "border-box",
                        overflowX: "hidden",
                    }}
                >
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                        <Typography variant="h6">Search</Typography>
                        <IconButton onClick={toggleDrawer(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Divider />
                    <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <Box sx={{ p: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Items to Include
                            </Typography>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                    gap: 1.25,
                                }}
                            >
                                <Box>
                                    <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "text.secondary" }}>
                                        Images
                                    </Typography>
                                    <ToggleButtonGroup
                                        value={imageStatus ?? "any"}
                                        exclusive
                                        size="small"
                                        fullWidth
                                        onChange={(_, v) => v && handleFilterChange("imageStatus", v)}
                                        sx={{
                                            "& .MuiToggleButton-root": {
                                                py: 0.5,
                                                px: 1,
                                                fontSize: "0.8rem",
                                                textTransform: "none",
                                            },
                                        }}
                                    >
                                        <ToggleButton value="any">Any</ToggleButton>
                                        <ToggleButton value="with">With</ToggleButton>
                                        <ToggleButton value="without">Without</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "text.secondary" }}>
                                        Acquisition Value
                                    </Typography>
                                    <ToggleButtonGroup
                                        value={acquisitionValueStatus ?? "any"}
                                        exclusive
                                        size="small"
                                        fullWidth
                                        onChange={(_, v) => v && handleFilterChange("acquisitionValueStatus", v)}
                                        sx={{
                                            "& .MuiToggleButton-root": {
                                                py: 0.5,
                                                px: 1,
                                                fontSize: "0.8rem",
                                                textTransform: "none",
                                            },
                                        }}
                                    >
                                        <ToggleButton value="any">Any</ToggleButton>
                                        <ToggleButton value="with">With</ToggleButton>
                                        <ToggleButton value="without">Without</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "text.secondary" }}>
                                        Insurance Value
                                    </Typography>
                                    <ToggleButtonGroup
                                        value={insuranceValueStatus ?? "any"}
                                        exclusive
                                        size="small"
                                        fullWidth
                                        onChange={(_, v) => v && handleFilterChange("insuranceValueStatus", v)}
                                        sx={{
                                            "& .MuiToggleButton-root": {
                                                py: 0.5,
                                                px: 1,
                                                fontSize: "0.8rem",
                                                textTransform: "none",
                                            },
                                        }}
                                    >
                                        <ToggleButton value="any">Any</ToggleButton>
                                        <ToggleButton value="with">With</ToggleButton>
                                        <ToggleButton value="without">Without</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "text.secondary" }}>
                                        Fair Market Value
                                    </Typography>
                                    <ToggleButtonGroup
                                        value={fairMarketValueStatus ?? "any"}
                                        exclusive
                                        size="small"
                                        fullWidth
                                        onChange={(_, v) => v && handleFilterChange("fairMarketValueStatus", v)}
                                        sx={{
                                            "& .MuiToggleButton-root": {
                                                py: 0.5,
                                                px: 1,
                                                fontSize: "0.8rem",
                                                textTransform: "none",
                                            },
                                        }}
                                    >
                                        <ToggleButton value="any">Any</ToggleButton>
                                        <ToggleButton value="with">With</ToggleButton>
                                        <ToggleButton value="without">Without</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                            </Box>
                        </Box>
                        <Divider />
                        <TextField
                            label="Description"
                            value={filters.description}
                            onChange={(e) => handleChange("description", e.target.value)}
                            fullWidth
                        />
                        <Divider />
                        {/* Selected categories summary */}
                        <Box sx={{ p: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>Categories</Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {categoryIds.length ? categoryIds.map((id) => (
                                    <Chip key={id} label={idToCatLabel.get(id) ?? String(id)} onDelete={() => removeCategory(id)} />
                                )) : <Typography variant="body2" sx={{ color: "text.secondary" }}>None selected</Typography>}
                            </Stack>
                            <Box sx={{ mt: 1 }}>
                                <Button size="small" variant="outlined" onClick={() => setCatPickerOpen(true)}>
                                    Choose categories…
                                </Button>
                                {categoryIds.length > 0 && (
                                    <Button size="small" sx={{ ml: 1 }} onClick={() => setCategoryIds([])}>
                                        Clear
                                    </Button>
                                )}
                            </Box>
                        </Box>
                        <FormControl fullWidth>
                            {/* Category picker drawer */}
                            <DrawerCategoryMulti
                                open={catPickerOpen}
                                onClose={() => setCatPickerOpen(false)}
                                categories={categories ?? []}
                                selectedIds={categoryIds}
                                onChange={setCategoryIds}
                                onApply={(ids) => { setCategoryIds(ids); setCatPickerOpen(false); }}
                                title="Choose categories"
                                allowMultiSelect={true}
                            />
                        </FormControl>
                        <Divider />
                        {/* Status filter */}
                        <Box sx={{ p: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>Status</Typography>
                            <Autocomplete
                                multiple
                                size="small"
                                options={ARTWORK_STATUSES}
                                groupBy={(option) => ARTWORK_STATUS_CATEGORY_LABELS[option.category] ?? "Other"}
                                getOptionLabel={(option) => option.name}
                                isOptionEqualToValue={(option, value) => option.artworkStatusId === value.artworkStatusId}
                                value={selectedStatuses}
                                onChange={(_, newValue) => handleStatusChange(newValue)}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder={selectedStatuses.length ? "" : "Any status"} />
                                )}
                            />
                        </Box>
                        <Divider />
                        {/* Selected artists summary */}
                        <Box sx={{ p: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>Artist Filter</Typography>
                            <ArtistFilter
                                artists={availableArtists}
                                selectedIds={artistIds}
                                onChange={handleArtistChange}
                            />
                        </Box>
                        <Divider />
                        {/* Selected locations summary */}
                        <Box sx={{ p: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>Locations</Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {locationIds.length ? locationIds.map((id) => (
                                    <Chip key={id} label={idToLabel.get(id) ?? String(id)} onDelete={() => removeLocation(id)} />
                                )) : <Typography variant="body2" sx={{ color: "text.secondary" }}>None selected</Typography>}
                            </Stack>
                            <Box sx={{ mt: 1 }}>
                                <Button size="small" variant="outlined" onClick={() => setLocPickerOpen(true)}>
                                    Choose locations…
                                </Button>
                                {locationIds.length > 0 && (
                                    <Button size="small" sx={{ ml: 1 }} onClick={() => setLocationIds([])}>
                                        Clear
                                    </Button>
                                )}
                            </Box>
                        </Box>
                        <FormControl fullWidth>
                            {/* Location picker drawer */}
                            <DrawerLocationsMulti
                                open={locPickerOpen}
                                onClose={() => setLocPickerOpen(false)}
                                locations={locations ?? []}
                                selectedIds={locationIds}
                                onChange={setLocationIds}
                                onApply={(ids) => { setLocationIds(ids); setLocPickerOpen(false); }}
                                title="Choose locations"
                            />
                        </FormControl>
                        <Divider />
                        {(isFinance &&
                            <>
                                <Box sx={{ p: 2 }}>
                                    <Typography variant="subtitle1" gutterBottom>Value Filter</Typography>
                                    <ValueRangeFilter
                                        currencies={currencies ?? []}
                                        defaultCurrency={defaultCurrency}
                                        initial={valueFilter ?? undefined}
                                        onChange={handleValueFilterChange}
                                    />
                                </Box>
                                <Divider />
                            </>
                        )}
                        <Box sx={{ p: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>Acquired Date Filter</Typography>
                            <DateRangeFilter
                                from={acquiredFromDateFilter ?? null}
                                to={acquiredToDateFilter ?? null}
                                onChange={handleDateRangeChange}
                            />
                        </Box>
                        <Divider />
                        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleClearSearch}
                            >
                                Clear
                            </Button>
                            <Button variant="contained" color="primary" onClick={handleSearch} fullWidth>
                                Apply Filter
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Drawer>
        </>
    );
};
